import React, { useState, useEffect } from 'react';
import ReportFilterBar from '../components/ReportFilterBar';
import ReportTable from '../components/ReportTable';
import ReportDownloadButtons from '../components/ReportDownloadButtons';
import { API_BASE_URL } from '../services/api';

const BuybackReportPage = () => {
  // State for filters, results, pagination, loading, and errors
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPortfolioBalance, setTotalPortfolioBalance] = useState(null);

  // Handler for filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handler for generating the report (calls backend API)
  const handleGenerateReport = async (filtersArg) => {
    const usedFilters = filtersArg || filters;
    if (usedFilters.view === 'transactions') {
      if (!usedFilters.dateFrom || !usedFilters.dateTo) {
        setError('Please select Date From and Date To before generating the transactions report.');
        return;
      }
    } else if (!usedFilters.asAtDate && !usedFilters.isin) {
      setError('Please select an "As at Date" or an ISIN before generating the report.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...usedFilters,
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      const res = await fetch(`${API_BASE_URL}/reports/buyback?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const json = await res.json();
      setResults(json.data || []);
      setPagination(p => ({ ...p, total: json.total || 0 }));
      setTotalPortfolioBalance(json.totalPortfolioBalance || null);
    } catch (e) {
      setError(e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  // Handler for page change
  const handlePageChange = async (newPagination) => {
    setPagination(newPagination);
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...filters,
        page: newPagination.page,
        pageSize: newPagination.pageSize
      });
      const res = await fetch(`${API_BASE_URL}/reports/buyback?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const json = await res.json();
      setResults(json.data || []);
      setPagination(p => ({ ...newPagination, total: json.total || 0 }));
      setTotalPortfolioBalance(json.totalPortfolioBalance || null);
    } catch (e) {
      setError(e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  // Handler for download
  const handleDownload = async (format) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...filters,
        format
        // Remove pagination for downloads to get all data
      });
      const res = await fetch(`${API_BASE_URL}/reports/buyback?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to download report');
      const blob = await res.blob();
      // Create a link to download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filters.view === 'transactions' ? 'buyback_transactions_report' : 'buyback_report'}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Error downloading report');
    } finally {
      setLoading(false);
    }
  };

  if (error) return (
    <div className="ui-page">
      <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">Error: {error}</div>
    </div>
  );

  return (
    <div className="ui-page">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        {filters.view === 'transactions'
          ? 'Buyback Transactions Report'
          : 'Buyback Report - Buy/Sell and Sell/Buy Transactions'}
      </h2>
      <ReportFilterBar
        filters={filters}
        onChange={handleFilterChange}
        onGenerate={handleGenerateReport}
        reportType="buyback"
        showReportView
      />
      <ReportDownloadButtons onDownload={handleDownload} disabled={loading || results.length === 0} />
      <ReportTable
        data={results}
        pagination={pagination}
        loading={loading}
        error={error}
        onPageChange={handlePageChange}
        reportType="buyback"
      />
      
      {/* Total Portfolio Balance Summary */}
      {filters.view !== 'transactions' && totalPortfolioBalance && (
        <div className="mt-5 p-4 bg-gray-50 border border-gray-200 rounded-lg text-right">
          <h5 className="m-0 mb-2 text-gray-600">
            Total Portfolio Balance: 
            <span className="ml-2 text-xl font-bold text-primary">
              {totalPortfolioBalance}
            </span>
          </h5>
          <small className="text-gray-500">
            Sum of all ISIN balances for portfolio: {filters.portfolio}
          </small>
        </div>
      )}
      
    </div>
  );
};

export default BuybackReportPage;
